import { IRREGULAR_BASES, PAST_FORMS } from './irregularForms.js';

/**
 * Answer matcher for free-text exercises (`translate_typed`, `listen_type`,
 * and the flashcards' typed cards).
 *
 * Matching rules (from PLAN.md § Phase 4, amended 2026-10-07):
 *   - Exact match after whitespace-collapse + case-fold → `exact`
 *   - Alt-answers widen the accepted set → `exact`
 *   - Short and full forms are the same answer: "you're" = "you are",
 *     "can't" = "cannot" = "can not" → `exact` (GRAMMAR-REVIEW § 3)
 *   - Levenshtein distance of 1 → `close` (user gets another try, not a fail)
 *     … UNLESS the one-letter change turns a word into ANOTHER REAL WORD the
 *     course teaches ("works" → "work", "an" → "a", "he" → "she",
 *     "sisters" → "sister"). That is a grammar or meaning mistake, not a
 *     typo → `wrong`. Pass `knownWords` to enable this check.
 *   - Otherwise → `wrong`
 *
 * Punctuation and trailing symbols are softened but not erased — the canonical
 * answer's punctuation is what counts. "My name is Mariam" matches
 * "My name is Mariam." because trailing `.` `!` `?` are dropped on both sides.
 */

export type MatchVerdict = 'exact' | 'close' | 'wrong';

export interface MatchArgs {
  input: string;
  answer: string;
  altAnswers?: readonly string[];
  /**
   * Every English word the course uses (see content/knownWords.ts). When
   * given, a one-letter change that lands on another known word is graded
   * `wrong` instead of `close`. Omit it and the matcher behaves as before.
   */
  knownWords?: ReadonlySet<string>;
  /** The words the course uses as verbs (content/knownWords.ts getVerbWords).
   *  When given, an added -s / -d counts as grammar only on these. */
  verbs?: ReadonlySet<string>;
  /** The words the course uses as nouns (getNounWords): an added -s is
   *  grammar on these too ("a teachers"). */
  nouns?: ReadonlySet<string>;
}

/** Phone keyboards type ’ (iOS smart punctuation), not '. */
const APOSTROPHES = /[‘’ʼ`´]/gu;

/**
 * Normalize user / canonical strings for comparison.
 * - Fold curly / typographic apostrophes to '
 * - Trim + collapse internal whitespace to a single space
 * - Lowercase (case-insensitive matching)
 * - Strip a single trailing sentence-ending punctuation (. ! ?)
 *
 * We deliberately keep internal punctuation (commas, apostrophes) so that
 * "whats your name" does NOT count as exact match for "what's your name".
 * That one will fall through to Levenshtein distance 1 → `close`.
 */
export function normalizeAnswer(raw: string): string {
  let s = raw.replace(APOSTROPHES, "'").replace(/\s+/g, ' ').trim().toLowerCase();
  s = s.replace(/[.!?]+$/u, '').trimEnd();
  return s;
}

/** Classic Levenshtein distance. Early-exit once distance exceeds `max`. */
export function levenshtein(a: string, b: string, max = Infinity): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const rows = a.length + 1;
  const cols = b.length + 1;
  let prev = new Array<number>(cols);
  let curr = new Array<number>(cols);
  for (let j = 0; j < cols; j++) prev[j] = j;
  for (let i = 1; i < rows; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[cols - 1];
}

/* --------------------------- short / full forms --------------------------- */

/** Unambiguous negative short forms. */
const NOT_FORMS: Record<string, readonly string[]> = {
  "isn't": ['is not'],
  "aren't": ['are not'],
  "wasn't": ['was not'],
  "weren't": ['were not'],
  "don't": ['do not'],
  "doesn't": ['does not'],
  "didn't": ['did not'],
  "haven't": ['have not'],
  "hasn't": ['has not'],
  "hadn't": ['had not'],
  "won't": ['will not'],
  "can't": ['cannot', 'can not'],
  cannot: ['can not'],
  "couldn't": ['could not'],
  "shouldn't": ['should not'],
  "wouldn't": ['would not'],
  "mustn't": ['must not'],
  "needn't": ['need not'],
  "let's": ['let us'],
};

/** Words that take 's / 'd as "is/has" / "would/had". Names are left out on
 *  purpose: "Mariam's" is far more often the possessive. */
const PRONOUN_HOSTS = new Set([
  'he', 'she', 'it', 'that', 'this', 'what', 'where', 'who', 'there', 'here',
  'how', 'when', 'why', 'everything', 'nothing', 'everyone', 'someone',
  'i', 'you', 'we', 'they',
]);

/** After 's / 'd these words mean has / had ("she's got", "he's been");
 *  other words mean is / would ("she's late", "I'd like"). One reading per
 *  short form — offering both let "She is got a dog" pass for "She's got a
 *  dog" (review 2026-10-08). */
const PERFECT_NEXT = new Set([
  'got', 'been', 'gone', 'had', 'seen', 'eaten', 'given', 'met', 'found', 'bought',
  'forgotten', 'heard', 'sent', 'told', 'won', 'spent', 'brought', 'caught',
  'taught', 'thought', 'become', 'flown', 'driven', 'swum', 'sung', 'begun', 'worn',
  'visited', 'tried', 'started', 'lived', 'worked', 'played', 'travelled', 'traveled',
  'arrived', 'stopped', 'studied', 'learned', 'learnt', 'decided',
]);
/** Participles that are just as often adjectives / passives after "is"
 *  ("it's broken", "she's called Mariam", "it's done"): both readings. */
const EITHER_NEXT = new Set([
  'done', 'made', 'taken', 'written', 'known', 'lost', 'broken', 'left', 'finished',
  'cooked', 'cleaned', 'called', 'booked', 'invited', 'drunk', 'closed', 'opened',
]);
/** Skipped when reading what follows 's / 'd: "he's never been" → has. */
const ADVERBS = new Set([
  'never', 'ever', 'just', 'already', 'always', 'still', 'also', 'not', 'too', 'really',
  'usually', 'often', 'sometimes', 'rarely', 'seldom', 'hardly', 'almost', 'even', 'only',
]);

/**
 * The full form(s) of ONE short-form token — usually one, two only for an
 * EITHER_NEXT participle; empty if it is not a short form. `rest` is the
 * words after it. A positive short form at the end of the clause ("Yes,
 * I'm." / "Yes, I'm just.") is not English, so it has no full form there —
 * the learner must write "Yes, I am."
 */
export function fullForms(token: string, rest: readonly string[], nounS = false): string[] {
  const fixed = NOT_FORMS[token];
  if (fixed) return [...fixed];
  const m = /^([a-z]+)'(m|re|ve|ll|s|d)$/u.exec(token);
  if (!m) return [];
  const next = bareWord(rest.find((w) => !ADVERBS.has(bareWord(w)) || bareWord(w) === 'not') ?? '');
  if (next === '') return []; // clause-final
  const host = m[1];
  const tail = m[2];
  const reading = (perfect: string, plain: string): string[] =>
    PERFECT_NEXT.has(next) ? [perfect] : EITHER_NEXT.has(next) ? [plain, perfect] : [plain];
  switch (tail) {
    case 'm':
      return host === 'i' ? ['i am'] : [];
    case 're':
      return [`${host} are`];
    case 've':
      return [`${host} have`];
    case 'll':
      return [`${host} will`];
    case 's':
      // A noun's 's is read only on the LEARNER's side (nounS): "my brother's
      // a driver" = "my brother is a driver". On the answer side "Mariam's
      // car" is the possessive, and "Mariam is car" must not pass.
      return PRONOUN_HOSTS.has(host) || nounS ? reading(`${host} has`, `${host} is`) : [];
    case 'd':
      // "you'd better" = had better; after 's, "better" is just "is better".
      if (!PRONOUN_HOSTS.has(host)) return [];
      return next === 'better' ? [`${host} had`] : reading(`${host} had`, `${host} would`);
    default:
      return [];
  }
}

/** Alternatives for one token (always includes the token itself). */
function tokenForms(token: string, rest: readonly string[], nounS: boolean): readonly string[] {
  return [token, ...fullForms(token, rest, nounS)];
}

/** Short forms typed without the apostrophe ("dont", "shes", "ive"). Only
 *  ones that are not real words themselves (its, ill, well, were, shed…). */
const NO_APOSTROPHE: Record<string, string> = {
  dont: "don't", doesnt: "doesn't", didnt: "didn't", isnt: "isn't", arent: "aren't",
  wasnt: "wasn't", werent: "weren't", havent: "haven't", hasnt: "hasn't",
  hadnt: "hadn't", cant: "can't", wont: "won't", couldnt: "couldn't",
  shouldnt: "shouldn't", wouldnt: "wouldn't", mustnt: "mustn't", im: "i'm",
  ive: "i've", youre: "you're", youve: "you've", theyre: "they're", theyve: "they've",
  weve: "we've", shes: "she's", hes: "he's", thats: "that's", whats: "what's",
  wheres: "where's", theres: "there's", heres: "here's", whos: "who's", lets: "let's",
  youll: "you'll", theyll: "they'll", itll: "it'll",
};

/** Short forms without the apostrophe that are ALSO real words ("its",
 *  "were", "well"…): restored one at a time, only to find a typo. */
const NO_APOSTROPHE_AMBIGUOUS: Record<string, string> = {
  its: "it's", were: "we're", well: "we'll", ill: "i'll", id: "i'd",
  hell: "he'll", shell: "she'll", shed: "she'd", wed: "we'd",
};

/** The learner's answer with lost apostrophes put back, for the typo check:
 *  every unambiguous one, then each ambiguous one on its own on top. */
function restoreApostrophes(normalized: string): string[] {
  const words = normalized.split(' ').map((w) => {
    const bare = bareWord(w);
    const fixed = NO_APOSTROPHE[bare];
    return fixed ? w.replace(bare, fixed) : w;
  });
  const out = [words.join(' ')];
  words.forEach((w, i) => {
    const bare = bareWord(w);
    const fixed = NO_APOSTROPHE_AMBIGUOUS[bare];
    if (fixed) out.push(words.map((x, j) => (j === i ? x.replace(bare, fixed) : x)).join(' '));
  });
  return out.filter((v) => v !== normalized);
}

/** Pronoun + short-form tail pairs that exist in English. "I's", "he're",
 *  "they's" are not short forms — a slip there is a typo. */
function isRealShortForm(host: string, tail: string): boolean {
  if (host === 'i') return tail === 'm' || tail === 've' || tail === 'll' || tail === 'd';
  if (host === 'you' || host === 'we' || host === 'they') return tail !== 'm' && tail !== 's';
  return PRONOUN_HOSTS.has(host) && (tail === 's' || tail === 'll' || tail === 'd');
}

/** The answer's full forms written short ("she is" → "she's", "is not" →
 *  "isn't"), so a typo INSIDE the learner's short form ("she;s") is one edit
 *  from the answer, not two. Used only for the typo check. */
function contracted(form: string): string {
  return form
    .replace(/\bi am\b/gu, "i'm")
    .replace(/\b(you|we|they) are\b/gu, "$1're")
    .replace(/\b(i|you|we|they) have (?=(\S+))/gu, (m, h: string, next: string) =>
      PERFECT_NEXT.has(bareWord(next)) ? `${h}'ve ` : m)
    .replace(/\b(he|she|it|that|what|where|who|there|here|how) is\b/gu, "$1's")
    .replace(/\b(he|she|it) has (?=(\S+))/gu, (m, h: string, next: string) =>
      PERFECT_NEXT.has(bareWord(next)) ? `${h}'s ` : m)
    .replace(/\b(i|you|he|she|it|we|they) will\b/gu, "$1'll")
    .replace(/\b(i|you|he|she|we|they) would\b/gu, "$1'd")
    .replace(/\bwill not\b/gu, "won't")
    .replace(/\b(?:can not|cannot)\b/gu, "can't")
    .replace(/\b(is|are|was|were|do|does|did|have|has|had|could|should|would|must) not\b/gu, "$1n't");
}

/** Words that never take a grammar -s / -d ending: a final s or d on them
 *  is a slip of the finger ("ands", "thed", "isd"), not grammar. */
const CLOSED_WORDS = new Set([
  'a', 'an', 'the', 'this', 'that', 'these', 'those', 'and', 'or', 'but', 'so', 'if',
  'in', 'on', 'at', 'to', 'of', 'for', 'with', 'from', 'by', 'about', 'is', 'am', 'are',
  'was', 'were', 'be', 'been', 'he', 'she', 'it', 'we', 'they', 'i', 'you', 'me', 'him',
  'her', 'us', 'them', 'my', 'your', 'his', 'our', 'their', 'its', 'here', 'there',
  'where', 'when', 'what', 'who', 'how', 'why', 'now', 'then', 'not', 'no', 'yes',
  'very', 'too', 'also', 'do', 'does', 'did', 'can', 'will', 'would', 'should', 'must',
  'has', 'have', 'had', 'always', 'sometimes', 'perhaps', 'as', 'than',
]);
/** A past form or participle: irregular, or a regular -ed whose base the
 *  course knows ("worked", "dropped" → drop, "tried" → try). */
function isPastForm(word: string, knownWords: ReadonlySet<string>): boolean {
  if (PAST_FORMS.has(word)) return true;
  if (!word.endsWith('ed')) return false;
  const stem = word.slice(0, -2);
  return (
    knownWords.has(word.slice(0, -1)) || // lived → live
    knownWords.has(stem) || // worked → work
    (stem.length > 2 && stem.at(-1) === stem.at(-2) && knownWords.has(stem.slice(0, -1))) || // dropped
    (word.endsWith('ied') && knownWords.has(`${word.slice(0, -3)}y`)) // tried → try
  );
}

/** After these a pronoun short form cannot stand ("Who is shes?", "Can
 *  hes swim?"): a stray s there is a typo. */
const AFTER_NO_SHORT_FORM = new Set([
  'is', 'are', 'am', 'was', 'were', 'do', 'does', 'did', 'can', 'will', 'would', 'should',
  'must', 'has', 'have', 'had', 'where', 'who', 'what', 'how', 'when', 'why', 'there',
]);
/** "want" → "wont": a real word swap even though "won't" does not start
 *  with "want" (see isRealWordSwap). */
const CONFUSABLE: Record<string, string> = { want: 'wont' };

/** Hard cap so a sentence full of short forms can't explode combinatorially. */
const MAX_FORMS = 32;

/**
 * Every spelling of a normalized answer with its short forms written out
 * (and the original). "you're not" → ["you're not", "you are not"].
 * `learner`: also read a noun's 's ("my brother's a driver"). Exported for tests.
 */
export function answerForms(normalized: string, learner = false): string[] {
  const tokens = normalized.split(' ');
  let forms = [''];
  tokens.forEach((token, i) => {
    const alts = tokenForms(token, tokens.slice(i + 1), learner);
    const next: string[] = [];
    for (const f of forms) {
      for (const a of alts) {
        next.push(f ? `${f} ${a}` : a);
        if (next.length >= MAX_FORMS) break;
      }
      if (next.length >= MAX_FORMS) break;
    }
    forms = next;
  });
  // "cannot" ↔ "can not" also needs the joined direction.
  return [...new Set(forms.flatMap((f) => [f, f.replace(/\bcan not\b/gu, 'cannot')]))];
}

/* ----------------------------- real-word swaps ----------------------------- */

/** Strip the punctuation that hugs a word ("works," → "works"). */
export function bareWord(token: string): string {
  return token.replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, '');
}

/**
 * True when `input` differs from `target` by ONE word, and the learner's word
 * is itself a real word the course teaches — "he work" vs "he works",
 * "a apple" vs "an apple". A missing apostrophe ("dont") is a typo, never a
 * swap. Exported for tests.
 */
export function isRealWordSwap(
  input: string,
  target: string,
  knownWords: ReadonlySet<string>,
  verbs?: ReadonlySet<string>,
  nouns?: ReadonlySet<string>,
): boolean {
  const a = input.split(' ');
  const b = target.split(' ');
  if (a.length !== b.length) return false; // a lost or extra space: a typo
  let swapped: [string, string] | null = null;
  let at = -1;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue;
    if (swapped) return false; // more than one word differs
    // A word that lost its first letter to a hyphen or apostrophe ("-shirt")
    // is a typo, whatever is left of it.
    if (/^[-']/u.test(a[i] ?? '')) return false;
    swapped = [bareWord(a[i]), bareWord(b[i])];
    at = i;
  }
  if (!swapped) return false;
  const [typed, theirs] = swapped;
  if (typed === theirs) return false; // only punctuation differs
  // A lost apostrophe or hyphen ("dont", "tshirt") is a typo, not another word.
  if (typed.replace(/['-]/gu, '') === theirs.replace(/['-]/gu, '')) return false;
  // "cant" / "im" / "wont" ARE "can't" / "I'm" / "won't" — judge them as such,
  // but only against their own word ("can" → "cant"); "im" for "in" is a typo.
  // A pronoun short form can only stand as a subject — not last ("I am
  // heres"), not after a verb or question word ("Who is shes?").
  // Nor before a verb: "shes is", "hes lives" (but "shes got" can be she's).
  const prev = bareWord(a[at - 1] ?? '');
  const next = bareWord(a.slice(at + 1).find((w) => !ADVERBS.has(bareWord(w)) || bareWord(w) === 'not') ?? '');
  const restored = NO_APOSTROPHE[typed];
  const pronounForm = restored !== undefined && !NOT_FORMS[restored];
  const verbForm = (w: string) =>
    verbs !== undefined &&
    (verbs.has(w) || (w.endsWith('s') && (verbs.has(w.slice(0, -1)) || verbs.has(w.slice(0, -2)))));
  const nextIsVerb = AFTER_NO_SHORT_FORM.has(next) || (verbForm(next) && !PERFECT_NEXT.has(next));
  const canBeShort =
    !pronounForm || (at < a.length - 1 && !AFTER_NO_SHORT_FORM.has(prev) && !nextIsVerb);
  const mine =
    restored && canBeShort && (restored.startsWith(theirs) || CONFUSABLE[theirs] === typed)
      ? restored
      : typed;
  if (knownWords.has(mine) || NOT_FORMS[mine]) return true;
  // A real short form of a pronoun: "I'm like" for "I'd like", "he's" for
  // "she's". Names ("Giorgi'd") and non-forms ("I's") are typos.
  const host = /^([\p{L}]+)'(m|re|ve|ll|s|d)$/u.exec(mine);
  if (host && isRealShortForm(host[1] ?? '', host[2] ?? '')) return true;
  // A grammar ending added or dropped ("I eats", "he live", "my parent"),
  // even when the course never shows that exact form. Only where an ending
  // can be grammar: -d after an e (live → lived), -s not on a word like
  // "the" / "and" / "is", not on a past form, not on a doubled letter
  // ("goodd", "thiss"), and a dropped one only when what is left is a word
  // ("parent" yes; "frien", "dishe", "tenni" are typos).
  if (!CLOSED_WORDS.has(theirs) && !CLOSED_WORDS.has(mine)) {
    const verb = (w: string) => !verbs || verbs.has(w);
    const noun = nouns?.has(theirs) === true;
    const past = isPastForm(theirs, knownWords);
    if (mine === `${theirs}d` && theirs.endsWith('e') && verb(theirs) && !past) return true;
    if (mine === `${theirs}s` && !theirs.endsWith('s') && !past && (verb(theirs) || noun)) return true;
    // "takes" → "taked", "goes" → "goed": the -s swapped for a past -d.
    // Only where the -d makes a real past form a learner might reach for:
    // an irregular verb regularised ("taked", "goed") or an -e verb ("liked").
    // "playd", "hurtd" are typos.
    const base = theirs.endsWith('es') && verbs?.has(theirs.slice(0, -2)) ? theirs.slice(0, -2) : theirs.slice(0, -1);
    if (
      verbs !== undefined &&
      /^[a-z]+s$/u.test(theirs) &&
      mine === `${theirs.slice(0, -1)}d` &&
      mine.endsWith('ed') && // "taked", "goed" — never "singd"
      verbs.has(base) &&
      (IRREGULAR_BASES.includes(base) || base.endsWith('e'))
    ) {
      return true;
    }
    if (theirs === `${mine}d` && theirs.endsWith('ed') && knownWords.has(mine)) return true;
    if (theirs === `${mine}s` && !theirs.endsWith('ss') && knownWords.has(mine)) return true;
  }
  return false;
}

/* --------------------------------- matcher --------------------------------- */

export function matchAnswer({ input, answer, altAnswers = [], knownWords, verbs, nouns }: MatchArgs): MatchVerdict {
  const normalized = normalizeAnswer(input);
  if (normalized === '') return 'wrong';
  const candidates = [answer, ...altAnswers].map(normalizeAnswer);
  if (candidates.some((c) => c === normalized)) return 'exact';

  // Short vs full forms: "you're tired" = "you are tired".
  const mine = new Set(answerForms(normalized, true));
  const theirs = candidates.map((c) => answerForms(c));
  if (theirs.some((forms) => forms.some((f) => mine.has(f)))) return 'exact';

  // A short form typed without its apostrophe ("shes from armenia" for "She
  // is from Armenia.") is a typo — close, with the exact spelling shown.
  for (const restored of restoreApostrophes(normalized)) {
    const fixedForms = new Set(answerForms(restored, true));
    if (theirs.some((forms) => forms.some((f) => fixedForms.has(f)))) return 'close';
  }

  // Close = Levenshtein distance 1 against any spelling of any candidate
  // (short and full forms included). Capped at 1 so we bail out fast — no
  // point computing real distance for very off answers. A one-letter change
  // that makes another real word is not a typo.
  const close = theirs.some((forms) =>
    [...new Set([...forms, ...forms.map(contracted)])].some((cf) =>
      [...mine].some(
        (f) =>
          levenshtein(cf, f, 1) <= 1 && !(knownWords && isRealWordSwap(f, cf, knownWords, verbs, nouns)),
      ),
    ),
  );
  return close ? 'close' : 'wrong';
}

/* ------------------------------ answer diff ------------------------------ */

export interface DiffToken {
  /** The answer's word as written (original case + punctuation). */
  text: string;
  /** The learner's answer has no matching word here. */
  missing: boolean;
}

/** A word as the diff compares it: short forms written out ("he's" →
 *  he, is; "can't" / "cannot" → can, not), so "He's" in the learner's answer
 *  matches "He is" in the right one. */
function atoms(tokens: readonly string[], i: number): string[] {
  const bare = bareWord(normalizeAnswer(tokens[i] ?? ''));
  const full = fullForms(bare, tokens.slice(i + 1))[0] ?? bare;
  const words = full.replace(/\bcannot\b/gu, 'can not').split(' ');
  // "brother's" (has / is / possessive): host + 's, matched loosely below.
  const nounS = words.length === 1 ? /^(.+)'s$/u.exec(words[0] ?? '') : null;
  return nounS ? [nounS[1] ?? '', "'s"] : words;
}

/** Diff equality: a noun's 's stands for is / has ("my brother's got"). */
function same(a: string | undefined, b: string | undefined): boolean {
  if (a === b) return true;
  return (a === "'s" && (b === 'is' || b === 'has')) || (b === "'s" && (a === 'is' || a === 'has'));
}

/**
 * Mark the words of the right answer that the learner did not write, so the
 * result bar can show WHERE the mistake is ("He works in Tbilisi" with
 * "works" marked when they typed "He work in Tbilisi"). Word-level LCS on
 * short-form-expanded words; display keeps the answer's own spelling.
 */
export function diffAgainstAnswer(input: string, answer: string): DiffToken[] {
  const shown = answer.trim().split(/\s+/u);
  // The answer, word by word, each word owning one or more atoms.
  const owner: number[] = [];
  const want: string[] = [];
  shown.forEach((_, i) => {
    for (const a of atoms(shown, i)) {
      want.push(a);
      owner.push(i);
    }
  });
  const typed = normalizeAnswer(input).split(' ');
  const got = typed.flatMap((_, i) => atoms(typed, i));
  const n = want.length;
  const m = got.length;
  // lcs[i][j] = LCS length of want[i..] and got[j..]
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        same(want[i], got[j]) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const missing = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < n) {
    if (j < m && same(want[i], got[j])) {
      i++;
      j++;
    } else if (j < m && lcs[i][j + 1] >= lcs[i + 1][j]) {
      j++;
    } else {
      missing.add(owner[i]);
      i++;
    }
  }
  return shown.map((text, k) => ({ text, missing: missing.has(k) }));
}
